/**
 * Per-match decoration applied by `getData()` so the rest of the app
 * reads derived fields directly off each match instead of recomputing
 * them on every render.
 *
 * Two passes:
 *   • `decorateMatchDates` — adds `isoDate` + `localDateStamp`. Runs
 *     BEFORE `parseData()`.
 *   • `decorateMatchPath`  — adds `path` + `label`. Runs AFTER
 *     `parseData()` so contestant names reflect any string-replacements
 *     (the dynamic match route slugifies post-rename to resolve, so
 *     the decorated URL must match the post-rename slug or it 404s).
 *
 * `path` is root-relative WITHOUT a leading slash (e.g.
 * `'matches/argentina-algeria-06-17/'`). Callers stitch in `base` as
 * `` `${base}/${match.path}` ``. `base` is NOT embedded — adapter-static
 * rewrites it per output page.
 */

import { dateSlug, getDate, getMatchISODate } from '$utils/dates';

import slugify from '$utils/slugify';

/**
 * @param {unknown} matches - Anything; only `Array.isArray(matches)`
 *   inputs are decorated. Non-arrays are returned untouched so callers
 *   don't need defensive checks.
 * @returns {Array<object>} A fresh array of matches each carrying
 *   `isoDate` and `localDateStamp`, or the original input if it wasn't
 *   an array.
 */
export const decorateMatchDates = (matches) => {
  if (!Array.isArray(matches)) return matches;
  return matches.map((m) => ({
    ...m,
    isoDate: getMatchISODate(m?.matchInfo),
    localDateStamp: m?.matchInfo ? getDate(m.matchInfo) : undefined,
  }));
};

/**
 * Build the `{ path, label }` pair for a single match:
 *   • Both sides known → `path = 'matches/<home>-<away>-<date>/'`.
 *   • Only one side known → `path = '.'` (callers skip rendering a link).
 *   • Neither side known → `{ path: undefined, label: undefined }`.
 *
 * @param {object | null | undefined} match
 * @returns {{ path: string | undefined, label: string | undefined }}
 */
export const buildMatchPath = (match) => {
  const matchInfo = match?.matchInfo;
  const contestants =
    Array.isArray(matchInfo?.contestant) ? matchInfo.contestant : [];
  const home = contestants.find((c) => c?.position === 'home');
  const away = contestants.find((c) => c?.position === 'away');
  const homeSlug = home?.name ? slugify(home.name) : undefined;
  const awaySlug = away?.name ? slugify(away.name) : undefined;

  if (!homeSlug && !awaySlug) return { path: undefined, label: undefined };

  const label = `${homeSlug || 'TBD'} vs. ${awaySlug || 'TBD'}`;
  const path =
    homeSlug && awaySlug && matchInfo ?
      `matches/${homeSlug}-${awaySlug}-${dateSlug(matchInfo)}/`
    : '.';

  return { path, label };
};

/**
 * @param {unknown} matches - Anything; only `Array.isArray(matches)`
 *   inputs are decorated. Non-arrays are returned untouched.
 * @returns {Array<object>} A fresh array of matches each carrying
 *   `path` and `label`, or the original input if it wasn't an array.
 */
export const decorateMatchPath = (matches) => {
  if (!Array.isArray(matches)) return matches;
  return matches.map((m) => ({ ...m, ...buildMatchPath(m) }));
};
